import { motion } from "framer-motion";
import { Heart, Cake, Mountain, Briefcase, GraduationCap } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// All imagery: Indonesia-themed / face-less
const IMAGES = {
  // Wedding — traditional Javanese/Balinese wedding decor, floral aisle
  wedding:
    "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&w=940&q=85",
  // Birthday — candles on cake close-up, warm tones
  birthday:
    "https://images.unsplash.com/photo-1558636508-e0db3814bd1d?crop=entropy&cs=srgb&fm=jpg&w=940&q=85",
  // Trip — Bali rice terraces / tropical Indonesia landscape
  trip: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?crop=entropy&cs=srgb&fm=jpg&w=940&q=85",
  // Corporate — empty conference / auditorium stage
  corporate:
    "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&w=940&q=85",
  // Graduation — caps thrown from behind (no faces) / diplomas
  graduation:
    "https://images.unsplash.com/photo-1523580494863-6f3031224c94?crop=entropy&cs=srgb&fm=jpg&w=940&q=85",
};

const ICONS = {
  wedding: Heart,
  birthday: Cake,
  trip: Mountain,
  corporate: Briefcase,
  graduation: GraduationCap,
};

export default function Occasions() {
  const { t } = useLang();
  const items = [
    { k: "wedding", label: t.occasions.items.wedding },
    { k: "birthday", label: t.occasions.items.birthday },
    { k: "trip", label: t.occasions.items.trip },
    { k: "corporate", label: t.occasions.items.corporate },
    { k: "graduation", label: t.occasions.items.graduation },
  ];

  return (
    <section
      id="occasions"
      data-testid="occasions-section"
      className="bg-[#DDE7E4] py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-[11px] uppercase tracking-[0.3em] text-neutral-600"
            >
              {t.occasions.overline}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-3 font-['Outfit'] text-4xl font-light tracking-tight text-neutral-950 sm:text-5xl lg:text-6xl"
            >
              For Every <span className="italic font-['Playfair_Display']">Occasion.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-sm text-sm leading-relaxed text-neutral-700 lg:text-base"
          >
            {t.occasions.sub}
          </motion.p>
        </div>

        <div className="mt-16 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
          {items.map((it, i) => {
            const Icon = ICONS[it.k];
            return (
              <motion.div
                key={it.k}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                data-testid={`occasion-card-${it.k}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-neutral-200">
                  <img
                    src={IMAGES[it.k]}
                    alt={it.label}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-transparent transition-opacity duration-500 group-hover:opacity-80" />
                  <div className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/85 backdrop-blur-sm">
                    <Icon className="h-4 w-4 text-neutral-800" strokeWidth={1.5} />
                  </div>
                </div>
                <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-neutral-800">
                  {it.label}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
